import dayjs from "dayjs";
import {
  ArrowLeftToLineIcon,
  ArrowRightToLineIcon,
  MoreHorizontalIcon,
} from "lucide-react";
import { type FormEventHandler, forwardRef, type Key } from "react";

import { trpc } from "@blis/trpc/react";

import { RoleCondition } from "@/components/RoleCondition";
import { UserAvatar } from "@/components/UserAvatar";
import { Button } from "@/components/ui/button";
import { TextArea, TextField } from "@/components/ui/field";
import { Form } from "@/components/ui/form";
import { Menu, MenuItem, MenuTrigger } from "@/components/ui/menu";
import { NavList, NavListItem } from "@/components/ui/nav-list";
import { Text } from "@/components/ui/text";
import { toast } from "@/components/ui/toast";
import { useAuth } from "@/features/auth/hooks";
import type { TAccessionComment, TAccessionDetail } from "@/lib/types";
import { format } from "@/lib/utils";
import { mutationResponse } from "@/utils/trpc";

export function AccessionComments({
  accession,
  isInternal,
}: {
  accession: TAccessionDetail;
  isInternal?: boolean;
}) {
  const comments = trpc.accession.comments.list.useQuery({
    accessionId: accession.id,
    isInternal: isInternal ?? false,
  });

  return (
    <div className="p-3">
      <NavList
        items={comments.data || []}
        isLoading={comments.isPending}
        loadingItems={2}
        emptyState={null}
        className="border-0"
      >
        {(comment) => (
          <NavListItem
            asChild
            className="focus-visible:ring-0 hover:bg-transparent"
            key={comment.id}
          >
            <CommentItem comment={comment} key={comment.id} />
          </NavListItem>
        )}
      </NavList>

      <CommentForm accession={accession} isInternal={isInternal} />
    </div>
  );
}

const CommentItem = forwardRef<HTMLDivElement, { comment: TAccessionComment }>(
  ({ comment }, ref) => {
    const update = trpc.accession.comments.update.useMutation(
      mutationResponse({
        successMessage: "Comment moved",
        errorTitle: "Error moving comment",
      }),
    );

    const onAction = (action: Key) => {
      switch (action) {
        case "toNotes":
          update.mutate({
            id: comment.id,
            isInternal: true,
          });
          break;
        case "toComments":
          update.mutate({
            id: comment.id,
            isInternal: false,
          });
          break;
      }
    };

    return (
      <div className="group/navitem flex gap-2 mb-4 outline-hidden" ref={ref}>
        <div className="shrink-0 pt-2">
          <UserAvatar user={comment.createdBy} />
        </div>
        <div className="flex flex-col w-full p-3 rounded-md border shadow-xs group-focus-visible/navitem:ring-1 ring-ring">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 flex-wrap">
              <Text size="2" className="font-medium">
                {comment.createdBy?.username}
              </Text>
              <Text muted size="1" title={format(comment.createdAt)}>
                {dayjs(comment.createdAt).fromNow()}
              </Text>
            </div>
            <RoleCondition
              roles={[
                "blis_lab_assistant",
                "blis_lab_tech",
                "blis_lab_manager",
              ]}
            >
              <MenuTrigger>
                <Button size="icon-sm" variant="ghost">
                  <MoreHorizontalIcon />
                </Button>
                <Menu aria-label="Comment actions" onAction={onAction}>
                  {comment.isInternal ? (
                    <MenuItem id="toComments">
                      <ArrowLeftToLineIcon />
                      Move to comments
                    </MenuItem>
                  ) : (
                    <MenuItem id="toNotes">
                      <ArrowRightToLineIcon />
                      Move to notes
                    </MenuItem>
                  )}
                  {/* <Menu.Separator />
                  <Menu.Item isDanger>
                    <TrashIcon />
                    Delete comment
                  </Menu.Item> */}
                  {/* <Menu.Item>
                  <AlertOctagonIcon />
                  Create hold with comment
                </Menu.Item>
                <Menu.Item>
                  <CheckIcon />
                  Resolve hold with comment
                </Menu.Item>
                <Menu.Item isDanger>
                  <XCircleIcon />
                  Cancel test with comment
                </Menu.Item>
                <Menu.Item isDanger>
                  <XCircleIcon />
                  Cancel accession with comment
                </Menu.Item> */}
                </Menu>
              </MenuTrigger>
            </RoleCondition>
          </div>
          <pre className="mt-2 whitespace-pre-wrap font-sans text-sm">
            {comment.text}
          </pre>
        </div>
      </div>
    );
  },
);

function CommentForm({
  accession,
  isInternal,
}: {
  accession: TAccessionDetail;
  isInternal?: boolean;
}) {
  const { user } = useAuth();
  const profile = user?.profile;

  // Reset form when accession changes
  // useEffect(() => {
  //   reset({ accessionId: accession.id, comment: "" });
  // }, [accession.id, reset]);

  const create = trpc.accession.comments.create.useMutation({
    onSuccess() {
      // reset();
      toast.success("Comment added");
    },
    onError(err) {
      toast.error("Error adding comment", {
        description: err.message,
      });
    },
  });

  const onSubmit: FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();
    const formData = new FormData(event.target as HTMLFormElement);
    const data = Object.fromEntries(formData.entries());
    // Reset form fields
    (event.target as HTMLFormElement).reset();
    create.mutate({
      accessionId: accession.id,
      comment: data.comment as string,
      isInternal,
    });
  };

  return (
    <div className="flex gap-2 mb-4">
      <div className="shrink-0 pt-2">
        <UserAvatar user={profile} />
      </div>
      <div className="flex flex-col gap-2 w-full p-3 rounded-md border shadow-xs focus-within:shadow-md">
        <Form onSubmit={onSubmit}>
          <TextField isRequired aria-label="Comment" name="comment">
            <TextArea
              placeholder={
                isInternal ? "Add an internal note..." : "Add a comment..."
              }
              className="focus:ring-0 border-none p-0 outline-hidden ring-0 shadow-none resize-none"
              style={{ fieldSizing: "content" } as React.CSSProperties}
            />
          </TextField>
          <div className="flex justify-end mt-4">
            <Button
              variant="outline"
              type="submit"
              isPending={create.isPending}
            >
              {isInternal ? "Add note" : "Comment"}
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
}
